/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Root component of the Pedal Fleet Admin Hub. Manages application routing,
layout structure, and global state management. Provides the main navigation and 
authentication wrapper.
*/

import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import NotFound from "./pages/NotFound";
import RouteGuard from "./components/auth/RouteGuard";
import RestrictedPage from "./pages/auth/RestrictedPage";

// Authentication
import LoginPage from "./pages/auth/LoginPage";

// Other page imports
import DashboardPage from "./pages/dashboard/DashboardPage";
import BikesListPage from "./pages/bikes/BikesListPage";
import BikeDetailPage from "./pages/bikes/BikeDetailPage";
import AddBikePage from "./pages/bikes/AddBikePage";
import EditBikePage from "./pages/bikes/EditBikePage";
import UsersListPage from "./pages/users/UsersListPage";
import UserDetailPage from "./pages/users/UserDetailPage";
import EditUserPage from "./pages/users/EditUserPage";
import StatisticsPage from "./pages/statistics/StatisticsPage";
import SettingsPage from "./pages/settings/SettingsPage";
import MaintenancePage from "./pages/maintenance/MaintenancePage";
import LocationsPage from "./pages/locations/LocationsPage";
import MaintenanceIssueDetailPage from "./pages/maintenance/MaintenanceIssueDetailPage";
import BookingsPage from "./pages/bookings/Index";
import BikeBusPage from "./pages/bikeBus/BikeBusPage";
import TransactionsPage from "./pages/transactions";
import EmissionsPage from "./pages/emissions/EmissionsPage";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
    mutations: {
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner position="top-right" duration={2000} richColors />
        <Analytics /> 
        <BrowserRouter>
          <Routes>
            <Route path="/auth" element={<LoginPage />} />
            <Route path="/restricted" element={<RestrictedPage />} />
            
            {/* Simplify by using only RouteGuard for protected routes */}
            <Route element={
              <RouteGuard>
                <MainLayout />
              </RouteGuard>
            }>
              <Route index element={<DashboardPage />} />
              
              <Route path="bikes" element={<BikesListPage />} />
              <Route path="bikes/new" element={<AddBikePage />} />
              <Route path="bikes/:bikeId" element={<BikeDetailPage />} />
              <Route path="bikes/:bikeId/edit" element={<EditBikePage />} />

              <Route path="bookings" element={<BookingsPage />} />
              
              <Route path="bike-bus" element={<BikeBusPage />} />

              <Route path="users" element={<UsersListPage />} />
              <Route path="users/:userId" element={<UserDetailPage />} />
              <Route path="users/:userId/edit" element={<EditUserPage />} />
              
              <Route path="statistics" element={<StatisticsPage />} />
              
              <Route path="emissions" element={<EmissionsPage />} />
              
              <Route path="maintenance" element={<MaintenancePage />} />
              <Route path="maintenance/:issueId" element={<MaintenanceIssueDetailPage />} />
              
              <Route path="locations" element={<LocationsPage />} />
              
              <Route path="transactions" element={<TransactionsPage />} />
              
              <Route path="settings" element={<SettingsPage />} />
            </Route>
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
